/**
 * screens/Settings/components/SpendCategoriesCard
 * ──────────────────────────────────────────────────────────────────────────
 * Code, name, GL, state, and Deactivate.
 *
 * DEACTIVATE, NOT DELETE. A category that vanished would orphan every spend
 * that used it, and the ledger is append-only — there is nothing to rewrite
 * those rows to. So an inactive category stays visible here and stops appearing
 * in the spend form.
 *
 * Deactivate is a BUTTON, not a row click, because it acts on the row rather
 * than opening it. That is the design's rule for the whole product.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Button, DataTable, StatusPill, type Column } from '../../../components';
import { neutral } from '../../../theme/colors';
import { mono, sans, size as fontSize } from '../../../theme/typography';
import { ellipsisStyle } from '../../../theme/webStyle';
import type { Category } from '../../../schemas/config';
import SettingsCard from './SettingsCard';

export interface SpendCategoriesCardProps {
  categories: readonly Category[];
  onAdd: () => void;
  onDeactivate: (category: Category) => void;
}

export function SpendCategoriesCard({
  categories,
  onAdd,
  onDeactivate,
}: SpendCategoriesCardProps) {
  const columns = useMemo<Column<Category>[]>(
    () => [
      {
        key: 'code',
        header: 'Code',
        track: { fr: 0.7 },
        render: (category) => (
          <Text numberOfLines={1} style={styles.code}>
            {category.code}
          </Text>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        track: { fr: 1.4 },
        render: (category) => (
          <Text numberOfLines={1} style={styles.name}>
            {category.display_name}
          </Text>
        ),
      },
      {
        key: 'gl',
        header: 'GL',
        track: { fr: 1 },
        render: (category) => (
          <Text numberOfLines={1} style={styles.gl}>
            {category.gl_hint ?? '—'}
          </Text>
        ),
      },
      {
        key: 'state',
        header: 'State',
        track: { fr: 0.7 },
        render: (category) => (
          <StatusPill
            label={category.is_active ? 'active' : 'inactive'}
            tone={category.is_active ? 'positive' : 'neutral'}
          />
        ),
      },
      {
        key: 'action',
        header: '',
        track: { px: 110 },
        align: 'right',
        stackedLabel: '',
        render: (category) =>
          category.is_active ? (
            <Button
              label="Deactivate"
              size="xs"
              variant="outline"
              onPress={() => onDeactivate(category)}
            />
          ) : null,
      },
    ],
    [onDeactivate],
  );

  return (
    <SettingsCard
      title="Spend categories"
      action={<Button label="Add category" size="sm" variant="primary" onPress={onAdd} />}
    >
      <DataTable
        columns={columns}
        rows={categories}
        keyExtractor={(category) => category.id}
        empty={
          <View style={styles.empty}>
            <Text style={styles.emptyTitle}>No categories yet</Text>
            <Text style={styles.emptyHint}>
              Every spend must carry a category, so the spend form cannot open until one exists.
            </Text>
          </View>
        }
      />
    </SettingsCard>
  );
}

const styles = StyleSheet.create({
  code: { ...mono('400'), fontSize: fontSize.label, color: neutral.ink, ...ellipsisStyle },
  name: { ...sans('400'), fontSize: fontSize.body, color: neutral.ink, ...ellipsisStyle },
  gl: { ...mono('400'), fontSize: fontSize.meta, color: neutral.grey600, ...ellipsisStyle },
  empty: { paddingVertical: 26, paddingHorizontal: 15, alignItems: 'center', gap: 3 },
  emptyTitle: { ...sans('500'), fontSize: fontSize.button, color: neutral.ink },
  emptyHint: {
    ...sans('400'),
    fontSize: fontSize.meta,
    color: neutral.grey600,
    textAlign: 'center',
    maxWidth: 460,
  },
});

export default SpendCategoriesCard;
